import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react'
import { User } from '@supabase/supabase-js'
import { v4 as uuidv4 } from 'uuid'
import { supabase } from '../lib/supabase' // Only used for supabase.auth (client-side auth)
import { AuthContextType, AuthState, UserProfile } from '../types/auth'
import { backendApiService } from '../services/backendApiService'
import { favoritesApiService } from '../services/favoritesApiService'
import { setAuthToken } from '../lib/authTokenStore'
import { clientLogger } from '../utils/clientLogger'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    profile: null,
    guestSession: null,
    loading: true,
    isAuthenticated: false,
    isGuest: false,
  })
  // Track in-flight profile creation to prevent duplicate calls
  const profileCreationInProgress = useRef<Set<string>>(new Set())

  // Initialize auth state
  useEffect(() => {
    // Listen for auth changes (includes INITIAL_SESSION event on mount)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // Always update the token store immediately — synchronous, no I/O.
        // This must happen before any async work so BaseApiService never races
        // against a token refresh to obtain the current access token.
        setAuthToken(session?.access_token ?? null)
        clientLogger.debug({ tokenPresent: session?.access_token ? 'PRESENT' : 'NULL' }, 'setAuthToken called')
        clientLogger.debug({ event, userId: session?.user?.id }, 'Auth state change')

        // Only process specific events to avoid duplicate calls.
        // SIGNED_IN is restricted to /auth/callback: that's the only page where
        // exchangeCodeForSession() fires SIGNED_IN (PKCE + OAuth flows). For
        // email/password sign-in, handleUserSession is called explicitly in
        // signInWithEmail() after signInWithPassword() resolves, so no SIGNED_IN
        // handler is needed there. session !== null guards against a premature
        // null-session fire before Supabase writes the local session.
        // INITIAL_SESSION covers all other authenticated page loads.
        const shouldProcess = event === 'INITIAL_SESSION' ||
                             event === 'TOKEN_REFRESHED' ||
                             event === 'SIGNED_OUT' ||
                             event === 'USER_UPDATED' ||
                             (event === 'SIGNED_IN' && session !== null && window.location.pathname === '/auth/callback')

        if (!shouldProcess) {
          clientLogger.debug({ event }, 'Skipping event, not processing')
          return
        }

        if (session?.user) {
          await handleUserSession(session.user)
        } else {
          await checkGuestSession()
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  // Global 401 handler — when BaseApiService receives a 401, the token is expired
  // or invalid. Sign the user out so they get a re-auth prompt instead of silent failures.
  useEffect(() => {
    const handleSessionExpired = () => {
      clientLogger.warn({}, 'Session expired (401 from API) — signing out')
      supabase.auth.signOut().catch(() => {})
    }
    window.addEventListener('auth:session-expired', handleSessionExpired)
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired)
  }, [])

  const handleUserSession = async (user: User) => {
    try {
      // Check for guest session that needs promotion.
      // Guard: only attempt promotion when we actually have a guest session — prevents
      // double-promotion if SIGNED_IN fires more than once (e.g. INITIAL_SESSION + SIGNED_IN).
      const currentGuestSession = state.guestSession
      const guestToken = localStorage.getItem('guest_session_token')
      const isCurrentlyGuest = state.isGuest || (!!guestToken && !state.isAuthenticated)
      const guestUserId = isCurrentlyGuest ? (currentGuestSession?.id || guestToken) : null

      // Determine auth provider from user metadata
      const authProvider = user.app_metadata?.provider || 'email'
      const userEmail = user.email

      // Promote guest user or link to existing authenticated user
      if (guestUserId && userEmail) {
        try {
          clientLogger.info({}, 'Promoting guest user to authenticated')

          const promotionResult = await backendApiService.promoteUser(
            guestUserId,
            user.id,
            userEmail,
            authProvider
          )
          clientLogger.debug({ promotionResult }, 'Promotion result')

          // Use the userId from promotion result (might be existing user if email matched)
          const effectiveUserId = promotionResult.userId

          // Migrate guest favorites to authenticated user
          try {
            clientLogger.debug({}, 'Migrating guest favorites to user account')
            await favoritesApiService.migrateGuestFavorites(guestUserId, effectiveUserId)
            clientLogger.debug({}, 'Successfully migrated guest favorites')
          } catch (migrationError) {
            clientLogger.error({ err: migrationError instanceof Error ? migrationError : new Error(String(migrationError)) }, 'Failed to migrate guest favorites')
            // Don't block the promotion flow if migration fails
          }

          // Clean up guest session data
          localStorage.removeItem('guest_session_token')
          localStorage.removeItem('guest_session_data')

          // Fetch user profile with the effective user ID
          let profile = null
          try {
            const profileResponse = await backendApiService.getUserProfile(effectiveUserId)
            profile = profileResponse.data
          } catch (error) {
            clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Error fetching profile')
          }

          setState({
            user: user as any,
            profile: profile || null,
            guestSession: null,
            loading: false,
            isAuthenticated: true,
            isGuest: false,
          })

          return
        } catch (promotionError) {
          clientLogger.error({ err: promotionError instanceof Error ? promotionError : new Error(String(promotionError)) }, 'Guest promotion failed')
          // Fall through to normal authenticated user flow
        }
      }

      // Normal authenticated user flow (no guest session to promote)
      let profile = null
      try {
        // CRITICAL: Use Supabase ID to fetch profile during authentication
        // This queries: user_profiles WHERE supabase_user_id = auth.users.id
        // Returns profile with custom user_id which will be used for all future operations
        const profileResponse = await backendApiService.getUserProfileBySupabaseId(user.id)
        profile = profileResponse.data
      } catch (error) {
        clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Error fetching profile')
      }

      // If no profile exists, this is first-time OAuth user - call promote to create profile
      if (!profile && userEmail) {
        // Check if we're already creating a profile for this user
        if (profileCreationInProgress.current.has(user.id)) {
          clientLogger.debug({ userId: user.id }, 'Profile creation already in progress for user')
          // Wait a bit and try fetching again (profile might be created by parallel call)
          await new Promise(resolve => setTimeout(resolve, 500))
          try {
            const profileResponse = await backendApiService.getUserProfileBySupabaseId(user.id)
            profile = profileResponse.data
          } catch (retryError) {
            clientLogger.error({ err: retryError instanceof Error ? retryError : new Error(String(retryError)) }, 'Profile still not found after retry')
          }
        } else {
          // Mark this user as having profile creation in progress
          profileCreationInProgress.current.add(user.id)

          try {
            clientLogger.info({}, 'First-time OAuth user - creating profile via promote endpoint')

            const promotionResult = await backendApiService.promoteUser(
              null, // No guest session for OAuth first-time users
              user.id,
              userEmail,
              authProvider
            )
            clientLogger.debug({ promotionResult }, 'Profile creation result')

            // Fetch the newly created profile
            const effectiveUserId = promotionResult.userId
            try {
              const profileResponse = await backendApiService.getUserProfile(effectiveUserId)
              profile = profileResponse.data
            } catch (fetchError) {
              clientLogger.error({ err: fetchError instanceof Error ? fetchError : new Error(String(fetchError)) }, 'Error fetching newly created profile')
            }
          } catch (createError) {
            clientLogger.error({ err: createError instanceof Error ? createError : new Error(String(createError)) }, 'Failed to create profile for OAuth user')
          } finally {
            // Always remove from in-progress set
            profileCreationInProgress.current.delete(user.id)
          }
        }
      }

      setState({
        user: user as any,
        profile: profile || null,
        guestSession: null,
        loading: false,
        isAuthenticated: true,
        isGuest: false,
      })
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Error handling user session')
      setState(prev => ({ ...prev, loading: false }))
    }
  }

  const checkGuestSession = async () => {
    const guestToken = localStorage.getItem('guest_session_token')
    
    if (guestToken) {
      try {
        // Try to get from database first via backend API
        const response = await backendApiService.getGuestSession(guestToken)
        const guestSession = response.data

        if (guestSession && new Date(guestSession.expiresAt) > new Date()) {
          setState({
            user: null,
            profile: null,
            guestSession,
            loading: false,
            isAuthenticated: false,
            isGuest: true,
          })
          return
        }
      } catch (error) {
        clientLogger.debug({ err: error instanceof Error ? error : new Error(String(error)) }, 'Database not available, checking localStorage')
      }

      // Fallback to localStorage
      try {
        const storedSession = localStorage.getItem('guest_session_data')
        if (storedSession) {
          const guestSession = JSON.parse(storedSession)
          if (new Date(guestSession.expiresAt) > new Date()) {
            setState({
              user: null,
              profile: null,
              guestSession,
              loading: false,
              isAuthenticated: false,
              isGuest: true,
            })
            return
          }
        }
      } catch (error) {
        clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Error parsing stored guest session')
      }

      // Clean up expired or invalid sessions
      localStorage.removeItem('guest_session_token')
      localStorage.removeItem('guest_session_data')
    }

    setState({
      user: null,
      profile: null,
      guestSession: null,
      loading: false,
      isAuthenticated: false,
      isGuest: false,
    })
  }

  const signInWithGoogle = async () => {
    setState(prev => ({ ...prev, loading: true }))
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/auth/callback',
          scopes: 'https://www.googleapis.com/auth/calendar.events'
        }
      })
      if (error) throw error
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Google sign in error')
      setState(prev => ({ ...prev, loading: false }))
      throw error
    }
  }

  const signInWithApple = async () => {
    setState(prev => ({ ...prev, loading: true }))
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: window.location.origin + '/auth/callback',
        }
      })
      if (error) throw error
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Apple sign in error')
      setState(prev => ({ ...prev, loading: false }))
      throw error
    }
  }

  const signInWithEmail = async (email: string, password: string) => {
    setState(prev => ({ ...prev, loading: true }))
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      if (error) throw error
      // SIGNED_IN event is scoped to /auth/callback (PKCE flows only), so explicitly
      // call handleUserSession here for email/password sign-in.
      if (data.user) await handleUserSession(data.user)
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Email sign in error')
      setState(prev => ({ ...prev, loading: false }))
      throw error
    }
  }

  const signUpWithEmail = async (email: string, password: string, displayName: string) => {
    setState(prev => ({ ...prev, loading: true }))
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: displayName,
          },
          // Direct the confirmation link back to our PKCE callback handler.
          // The handler exchanges the code for a session then navigates to /app.
          emailRedirectTo: window.location.origin + '/auth/callback',
        }
      })
      if (error) throw error
      
      // Create user profile if user was created
      if (data.user) {
        try {
          // Generate a custom UUID for user_id (NOT Supabase auth ID).
          // This preserves our custom UUID architecture so guest promotion works.
          const customUserId = uuidv4()
          await backendApiService.createUserProfile(customUserId, {
            email: email,
            display_name: displayName,
            dietary_restrictions: [],
            dietary_preference: null,
            appliance_availability: [],
            setup_completed: false,
            is_guest: false,
            auth_providers: ['email'],
            supabase_user_id: data.user.id
          })
        } catch (profileError) {
          clientLogger.error({ err: profileError instanceof Error ? profileError : new Error(String(profileError)) }, 'Error creating profile')
        }
      }
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Email sign up error')
      setState(prev => ({ ...prev, loading: false }))
      throw error
    }
  }

  const signOut = async () => {
    setState(prev => ({ ...prev, loading: true }))
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      
      // Clear guest session if it exists
      localStorage.removeItem('guest_session_token')
      
      setState({
        user: null,
        profile: null,
        guestSession: null,
        loading: false,
        isAuthenticated: false,
        isGuest: false,
      })
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Sign out error')
      setState(prev => ({ ...prev, loading: false }))
      throw error
    }
  }

  const createGuestSession = async () => {
    setState(prev => ({ ...prev, loading: true }))
    try {
      const sessionToken = uuidv4()
      const expiresAt = new Date()
      expiresAt.setDate(expiresAt.getDate() + 7) // 7 days from now

      // Create guest session locally (fallback for when database tables don't exist yet)
      const guestSession = {
        id: sessionToken,
        sessionToken: sessionToken,
        preferences: {
          organic_only: false,
          budget_conscious: false,
          quick_meals: false,
          meal_prep: false,
          dietary_template: null,
          spice_level: 'medium',
          cooking_skill: 'beginner',
          kitchen_equipment: [],
        },
        mealHistory: [],
        createdAt: new Date().toISOString(),
        expiresAt: expiresAt.toISOString(),
      }

      // Save to database via backend API - fail fast if there's an issue
      try {
        await backendApiService.createGuestSession(guestSession)
      } catch (dbError) {
        clientLogger.error({ err: dbError instanceof Error ? dbError : new Error(String(dbError)) }, 'Failed to create guest session in database')
        throw new Error('Unable to start guest session. Please check your connection and try again.')
      }

      localStorage.setItem('guest_session_token', sessionToken)
      localStorage.setItem('guest_session_data', JSON.stringify(guestSession))
      
      setState({
        user: null,
        profile: null,
        guestSession,
        loading: false,
        isAuthenticated: false,
        isGuest: true,
      })
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Create guest session error')
      setState(prev => ({ ...prev, loading: false }))
      throw error
    }
  }

  const convertGuestToUser = async () => {
    if (!state.guestSession || !state.user) {
      throw new Error('No guest session or user to convert')
    }

    setState(prev => ({ ...prev, loading: true }))
    try {
      // Transfer guest data to user profile
      await backendApiService.updateUserProfile(state.user.id, {
        preferences: state.guestSession.preferences,
      })

      // Delete guest session
      await backendApiService.deleteGuestSession(state.guestSession.id)

      localStorage.removeItem('guest_session_token')
      localStorage.removeItem('guest_session_data')

      // Refresh profile data
      await refreshProfile()
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Convert guest to user error')
      setState(prev => ({ ...prev, loading: false }))
      throw error
    }
  }

  const updateProfile = async (updates: Partial<UserProfile>) => {
    const profileUserId = state.profile?.userId
    if (!profileUserId) {
      throw new Error('No user profile')
    }

    setState(prev => ({ ...prev, loading: true }))
    try {
      const response = await backendApiService.updateUserProfile(profileUserId, updates)

      setState(prev => ({
        ...prev,
        profile: response.data,
        loading: false,
      }))
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Update profile error')
      setState(prev => ({ ...prev, loading: false }))
      throw error
    }
  }

  const refreshProfile = async () => {
    const profileUserId = state.profile?.userId
    if (!profileUserId) return

    try {
      const response = await backendApiService.getUserProfile(profileUserId)

      setState(prev => ({
        ...prev,
        profile: response.data,
      }))
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Refresh profile error')
    }
  }

  const value: AuthContextType = {
    ...state,
    signInWithGoogle,
    signInWithApple,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    createGuestSession,
    convertGuestToUser,
    updateProfile,
    refreshProfile,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}